import { createRoot } from 'react-dom/client'
import { useEffect } from 'react'
import { Button } from '../components/ui'

interface ConfirmModalProps {
  message: string
  /** What the confirming button says. See `labelFor` for where it comes from. */
  label: string
  destructive: boolean
  onConfirm: () => void
  onCancel: () => void
}

function ConfirmModal({ message, label, destructive, onConfirm, onCancel }: ConfirmModalProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return (
    <div role="alertdialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="animate-modal-backdrop-in absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="animate-modal-card-in relative w-full max-w-sm rounded-2xl border border-line bg-pane p-6 shadow-2xl">
        <p className="text-[15px] leading-relaxed text-text">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button type="button" variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
            {label}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * What the confirming button should say, read off whatever triggered the
 * confirm.
 *
 * This dialog said "Delete", in red, for every `data-turbo-confirm` in the app.
 * That was true of every caller when it was written and stopped being true the
 * moment something non-destructive wanted a confirmation — a button that reads
 * "Delete" over a message about matching invoices is worse than no confirmation
 * at all, because the reader believes the button over the sentence.
 *
 * So a caller can name its own action with `data-confirm-label`, on the
 * submitter or the form, and gets the primary style with it. Anything that
 * doesn't is still a deletion and still says so, in red — which is why the
 * default stays rather than becoming a neutral "Confirm": the twenty existing
 * callers really are destructive, and quietly softening their button is the
 * opposite of what this dialog is for.
 */
function labelFor(form?: HTMLFormElement, submitter?: HTMLElement): { label: string; destructive: boolean } {
  const named = submitter?.dataset?.confirmLabel ?? form?.dataset?.confirmLabel

  return named ? { label: named, destructive: false } : { label: 'Delete', destructive: true }
}

// Bridges Turbo's confirm hook (wired up in entrypoints/application.ts via
// Turbo.config.forms.confirm) to a styled dialog instead of window.confirm.
// Each call mounts a throwaway root/container rather than reusing one, since
// these fire rarely and a fresh mount sidesteps any question of a stale node
// surviving a Turbo body swap between calls.
//
// Turbo hands over the form and the submitter alongside the message, which is
// what makes `data-confirm-label` readable here.
export function confirmModal(message: string, form?: HTMLFormElement, submitter?: HTMLElement): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)
    const { label, destructive } = labelFor(form, submitter)

    function cleanup(answer: boolean) {
      root.unmount()
      container.remove()
      resolve(answer)
    }

    root.render(
      <ConfirmModal
        message={message}
        label={label}
        destructive={destructive}
        onConfirm={() => cleanup(true)}
        onCancel={() => cleanup(false)}
      />
    )
  })
}
